import { defineStore } from 'pinia'
import { computed, ComputedRef, Ref, ref } from 'vue'
import { ExpenseReport, Expense } from '@/components/PaymentRequest/types'

export const useExpensesStore = defineStore('payment-request-expenses', () => {
  const enableExpenseReports = ref(false)
  const expenseReports: Ref<ExpenseReport[]> = ref([])

  const expenseReportLocalIds: ComputedRef<(number | string)[]> = computed(() =>
    expenseReports.value.map((expenseReport) => expenseReport.localId),
  )

  // Actions
  const updateExpenseReports = (updatedExpenseReports: ExpenseReport[]) => {
    expenseReports.value = [...updatedExpenseReports]
  }

  const updateExpenseReport = (updatedExpenseReport: ExpenseReport) => {
    const matchingExpenseReport = expenseReports.value.find(
      (expenseReport) => expenseReport.localId === updatedExpenseReport.localId,
    )
    if (!matchingExpenseReport) return

    matchingExpenseReport.title = updatedExpenseReport.title
    matchingExpenseReport.description = updatedExpenseReport.description
  }

  const unlinkExpenseReport = (expenseReportLocalId: string | number) => {
    const matchingExpenseReportIndex = expenseReports.value.findIndex(
      (expenseReport) => expenseReport.localId === expenseReportLocalId,
    )
    if (matchingExpenseReportIndex === -1) return
    expenseReports.value.splice(matchingExpenseReportIndex, 1)
  }

  const deleteExpenseReport = (expenseReportLocalId: string | number) => {
    expenseReports.value = [
      ...expenseReports.value.filter(
        (expenseReport) => expenseReport.localId !== expenseReportLocalId,
      ),
    ]
  }
  const deleteExpense = (expenseLocalId: string | number) => {
    const matchingExpenseReport = expenseReports.value.find((expenseReport) =>
      expenseReport.expenses.some((expense) => expense.localId === expenseLocalId),
    )

    if (!matchingExpenseReport) return

    matchingExpenseReport.expenses = [
      ...matchingExpenseReport.expenses.filter((expense) => expense.localId !== expenseLocalId),
    ]

    matchingExpenseReport.total = matchingExpenseReport.expenses.reduce(
      (total, expense) => total + expense.amount,
      0,
    )
  }

  const updateExpense = (updatedExpense: Expense, expenseReportLocalId: string | number) => {
    const matchingExpenseReportIndex = expenseReports.value.findIndex(
      (expenseReport) => expenseReport.localId === Number(expenseReportLocalId),
    )
    if (matchingExpenseReportIndex === -1) return

    const matchingExpenseReport = expenseReports.value[matchingExpenseReportIndex]

    // Find the index of the matching expense
    const expenseIndex = matchingExpenseReport.expenses.findIndex(
      (expense) => expense.localId === updatedExpense.localId,
    )
    if (expenseIndex === -1) return

    // Create a new array to trigger reactivity
    const updatedExpenses = [...matchingExpenseReport.expenses]
    updatedExpenses[expenseIndex] = { ...updatedExpense }

    // Update the report with a new reference
    expenseReports.value[matchingExpenseReportIndex] = {
      ...matchingExpenseReport,
      expenses: updatedExpenses, // Replace expenses array
      total: updatedExpenses.reduce((total, expense) => total + expense.amount, 0),
    }
  }

  const addExpense = (expense: Expense, expenseReportLocalId: string | number) => {
    const matchingExpenseReport = expenseReports.value.find(
      (expenseReport) => expenseReport.localId === expenseReportLocalId,
    )

    if (!matchingExpenseReport) return

    // Ensure expenses array exists
    if (!Array.isArray(matchingExpenseReport.expenses)) {
      matchingExpenseReport.expenses = []
    }

    // Add new expense to array
    matchingExpenseReport.expenses = [
      ...matchingExpenseReport.expenses,
      {
        ...expense,
        files: expense.files || [],
      },
    ]

    // Update total safely
    matchingExpenseReport.total = matchingExpenseReport.expenses.reduce(
      (total, e) => total + e.amount,
      0,
    )
  }

  const addExpenseReport = (expenseReport: ExpenseReport) => {
    expenseReports.value = [
      ...expenseReports.value,
      {
        title: expenseReport.title,
        description: expenseReport.description,
        expenses: expenseReport.expenses,
        total: 0,
        globalId: expenseReport.globalId,
        localId: expenseReport.localId,
      },
    ]
  }

  const clearExpenses = () => (expenseReports.value = [])

  return {
    enableExpenseReports,
    expenseReports,
    expenseReportLocalIds,
    updateExpenseReport,
    updateExpenseReports,
    unlinkExpenseReport,
    addExpenseReport,
    updateExpense,
    deleteExpenseReport,
    deleteExpense,
    addExpense,
    clearExpenses,
  }
})
